import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addWeeks, parseISO } from 'date-fns';
import { useId, useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { paths } from '@/app/router/paths';

import { Button, Skeleton, cn, focusRing } from '@/design-system';
import {
    legendServices,
    orderAccessFor,
    orderApi,
    orderKeys,
    planningApi,
    workWeekOf,
    type AutoScheduleResult,
    type PlanningSuggestion,
} from '@/entities/order';
import { PERMISSIONS, hasPermission } from '@/entities/user';
import { NEW_INSPECTION_PATH, inspectionsWithStatus } from '@/features/inspections/listLink';
import { errorMessage, problemKey, useSessionStore } from '@/shared/api';
import { formatDayShort } from '@/shared/lib';
import { ErrorState, InspectorPicker, Screen, ScreenBody, ScreenHeader } from '@/shared/ui';

import { AdviceBar } from './components/AdviceBar';
import { AutoSchedulePanel } from './components/AutoSchedulePanel';
import { ServiceLegend } from './components/ServiceLegend';
import { WeekGrid } from './components/WeekGrid';
import { planningCopy } from './copy';

type Notice = { tone: 'done' | 'problem'; message: string };

/**
 * Scherm 4 - Planning.
 *
 * Boven het rooster één concreet advies met één knop, daaronder het weekrooster
 * per inspecteur. Bladeren gaat per werkweek. Automatisch inplannen laat eerst
 * zien wat er zou gebeuren (`dry_run`) en voert pas daarna door.
 *
 * De inspecteurs op het bord zijn de actieve leden van déze omgeving; een
 * keurmeester die ook elders werkt telt hier alleen met zijn werk hier. Het filter
 * is dezelfde inspecteurskeuze als bij het inplannen, met Mijzelf bovenaan.
 *
 * De legenda noemt alleen de dienstkleuren van wat dit bedrijf levert (`GET
 * /inspection-types`) en wat er in beeld staat.
 *
 * Elk getal leidt ergens heen: *3 keuringen wachten op een afspraak* opent
 * Keuringen met precies die keuringen. Een bedrijf zonder inspecteurs krijgt
 * bij de lege week de twee stappen die het bord vullen, en Automatisch
 * inplannen staat dan uit, met de reden erbij.
 */
export function PlanningPage() {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const session = useSessionStore((state) => state.session);
    const canCreate = orderAccessFor(session).manage;
    const canInvite = hasPermission(session?.permissions ?? [], PERMISSIONS.usersInvite);
    const autoReasonId = useId();
    const [anchor, setAnchor] = useState(() => new Date());
    const [inspector, setInspector] = useState('');
    const [preview, setPreview] = useState<AutoScheduleResult | null>(null);
    const [notice, setNotice] = useState<Notice | null>(null);

    const range = workWeekOf(anchor);
    const planning = useQuery({
        queryKey: orderKeys.planning(range),
        queryFn: () => planningApi.week(range),
    });
    const inspectionTypes = useQuery({
        queryKey: orderKeys.inspectionTypes(),
        queryFn: () => orderApi.inspectionTypes(),
    });

    const refresh = () => {
        void queryClient.invalidateQueries({ queryKey: orderKeys.planningAll() });
        void queryClient.invalidateQueries({ queryKey: orderKeys.lists() });
        void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    };

    const apply = useMutation({
        mutationFn: (suggestion: PlanningSuggestion) => planningApi.applySuggestion(suggestion.id),
        onSuccess: (order) => {
            setNotice({ tone: 'done', message: planningCopy.adviceApplied(order.number) });
            refresh();
        },
        onError: (error) => {
            setNotice({
                tone: 'problem',
                message:
                    problemKey(error) === 'planning-suggestion-expired'
                        ? planningCopy.adviceExpired
                        : errorMessage(error),
            });
            refresh();
        },
    });

    const autoSchedule = useMutation({
        mutationFn: (dryRun: boolean) => planningApi.autoSchedule({ ...range, dryRun }),
        onSuccess: (result) => {
            if (result.dryRun) {
                setPreview(result);

                return;
            }

            setPreview(null);
            setNotice({ tone: 'done', message: planningCopy.autoDone(result.scheduled.length) });
            refresh();
        },
        onError: (error) => setNotice({ tone: 'problem', message: errorMessage(error) }),
    });

    const week = planning.data;
    const rows = week
        ? week.rows.filter((row) => inspector === '' || row.inspectorUuid === inspector)
        : [];
    const suggestion = week?.suggestions[0] ?? null;
    // Zonder inspecteurs op het bord valt er niemand in te plannen.
    const noInspectors = week !== undefined && week.rows.length === 0;

    function move(weeks: number | null) {
        setAnchor((current) => (weeks === null ? new Date() : addWeeks(current, weeks)));
        setPreview(null);
        setNotice(null);
    }

    return (
        <Screen>
            <ScreenHeader
                eyebrow={
                    week
                        ? planningCopy.eyebrow(
                              week.weekNumber,
                              formatDayShort(parseISO(week.from)),
                              formatDayShort(parseISO(week.to)),
                          )
                        : planningCopy.eyebrowFallback
                }
                title={planningCopy.title}
                actions={
                    <>
                        <div
                            className="flex gap-1"
                            role="group"
                            aria-label={planningCopy.weekNavigation}
                        >
                            <Button variant="onDark" size="sm" onClick={() => move(-1)}>
                                {planningCopy.previousWeek}
                            </Button>
                            <Button variant="onDark" size="sm" onClick={() => move(null)}>
                                {planningCopy.thisWeek}
                            </Button>
                            <Button variant="onDark" size="sm" onClick={() => move(1)}>
                                {planningCopy.nextWeek}
                            </Button>
                        </div>
                        <InspectorPicker
                            surface="onDark"
                            size="sm"
                            icon="filter"
                            className="min-h-[40px] max-w-[240px]"
                            aria-label={planningCopy.inspectorFilterLabel}
                            allLabel={planningCopy.inspectorAll}
                            value={inspector}
                            onChange={setInspector}
                        />
                        <Button
                            variant="primary"
                            size="sm"
                            disabled={autoSchedule.isPending || !week || noInspectors}
                            aria-describedby={noInspectors ? autoReasonId : undefined}
                            onClick={() => {
                                setNotice(null);
                                autoSchedule.mutate(true);
                            }}
                        >
                            {autoSchedule.isPending && preview === null
                                ? planningCopy.autoPreviewing
                                : planningCopy.autoSchedule}
                        </Button>
                    </>
                }
            />

            <ScreenBody>
                {notice ? (
                    <p
                        role={notice.tone === 'done' ? 'status' : 'alert'}
                        className={
                            notice.tone === 'done'
                                ? 'm-0 mb-[14px] text-[13px] leading-[1.5] text-[var(--accent-dark)]'
                                : 'm-0 mb-[14px] text-[13px] leading-[1.5] text-[var(--danger)]'
                        }
                    >
                        {notice.message}
                    </p>
                ) : null}

                {planning.isError ? (
                    <ErrorState
                        error={planning.error}
                        title={planningCopy.errorTitle}
                        onRetry={() => void planning.refetch()}
                    />
                ) : null}

                {preview ? (
                    <AutoSchedulePanel
                        preview={preview}
                        rows={week?.rows ?? []}
                        applying={autoSchedule.isPending}
                        onApply={() => autoSchedule.mutate(false)}
                        onClose={() => setPreview(null)}
                    />
                ) : null}

                {suggestion && !preview ? (
                    <AdviceBar
                        suggestion={suggestion}
                        busy={apply.isPending}
                        onApply={(chosen) => apply.mutate(chosen)}
                    />
                ) : null}

                {week && week.unscheduledCount > 0 ? (
                    <p className="m-0 mb-[14px] text-[13px]">
                        <Link
                            to={inspectionsWithStatus('wacht-op-afspraak')}
                            className={cn(
                                'rounded-[2px] text-[var(--accent)] underline underline-offset-2 hover:text-[var(--accent-dark)]',
                                focusRing,
                            )}
                        >
                            {planningCopy.unscheduled(week.unscheduledCount)}
                        </Link>
                    </p>
                ) : null}

                {planning.isPending ? (
                    <Skeleton height={280} radius={10} pulse label={planningCopy.title} />
                ) : week && rows.length > 0 ? (
                    <>
                        <WeekGrid week={week} rows={rows} />
                        <ServiceLegend
                            services={legendServices(rows, inspectionTypes.data ?? [])}
                        />
                    </>
                ) : noInspectors ? (
                    <div className="flex flex-col items-start gap-3">
                        <p className="m-0 text-[13px] text-[var(--text-tertiary-safe)]">
                            {planningCopy.emptyWeek}
                        </p>
                        {canInvite || canCreate ? (
                            <div className="flex flex-wrap gap-2">
                                {canInvite ? (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        icon="team"
                                        onClick={() => void navigate(paths.settingsTab('team'))}
                                    >
                                        {planningCopy.emptyWeekInvite}
                                    </Button>
                                ) : null}
                                {canCreate ? (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        icon="add"
                                        onClick={() => void navigate(NEW_INSPECTION_PATH)}
                                    >
                                        {planningCopy.emptyWeekNewInspection}
                                    </Button>
                                ) : null}
                            </div>
                        ) : null}
                        <p
                            id={autoReasonId}
                            className="m-0 text-[12px] text-[var(--text-tertiary-safe)]"
                        >
                            {planningCopy.autoNeedsInspector}
                        </p>
                    </div>
                ) : week ? (
                    <p className="m-0 text-[13px] text-[var(--text-tertiary-safe)]">
                        {planningCopy.emptyInspector}
                    </p>
                ) : null}
            </ScreenBody>
        </Screen>
    );
}
